<script setup>
import { defineProps, computed } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useAccountTimezone } from 'dashboard/composables/useAccountTimezone';
import {
  fromUnixTime,
  format,
  isSameYear,
  differenceInCalendarDays,
} from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { messageStamp } from 'shared/helpers/timeHelper';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);
const { accountTimezone } = useAccountTimezone();

const allMessages = computed(() => {
  return useCamelCase(props.messages, { deep: true });
});

const SESSION_GAP_SECONDS = 10 * 60;

const toZonedDate = unixSeconds => {
  const dt = fromUnixTime(unixSeconds);
  return accountTimezone.value ? utcToZonedTime(dt, accountTimezone.value) : dt;
};

const formatSessionDividerLabel = unixSeconds => {
  const dt = toZonedDate(unixSeconds);
  const now = accountTimezone.value
    ? utcToZonedTime(new Date(), accountTimezone.value)
    : new Date();
  const dayDiff = differenceInCalendarDays(now, dt);
  const dayLabel =
    dayDiff === 0
      ? 'Today'
      : dayDiff === 1
        ? 'Yesterday'
        : format(dt, isSameYear(dt, now) ? 'MMM d' : 'MMM d, yyyy');
  const timeLabel = messageStamp(unixSeconds, 'hh:mm a', accountTimezone.value);
  return `Started - ${dayLabel} ${timeLabel}`;
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;
  if (next.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (current.messageType === MESSAGE_TYPES.ACTIVITY) return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

/**
 * Determines if a message should be grouped with the previous message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with previous
 */
const shouldGroupWithPrev = (index, searchList) => {
  if (index === 0) return false;

  const prev = searchList[index - 1];
  const current = searchList[index];

  if (current.status === 'failed') return false;
  if (prev.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (current.messageType === MESSAGE_TYPES.ACTIVITY) return false;

  const prevSenderId = prev.senderId ?? prev.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = prevSenderId === currentSenderId;

  const prevMessageType = prev.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    prevMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;
  if (prevMessageType !== currentMessageType) return false;

  return Math.floor(prev.createdAt / 60) === Math.floor(current.createdAt / 60);
};

const renderItems = computed(() => {
  const items = [];
  const list = allMessages.value || [];

  list.forEach((message, index) => {
    const prev = list[index - 1];
    const isNewSession =
      index === 0 || !prev
        ? true
        : message.createdAt - prev.createdAt >= SESSION_GAP_SECONDS;

    if (isNewSession && message.messageType !== MESSAGE_TYPES.ACTIVITY) {
      items.push({
        kind: 'divider',
        key: `divider-${message.id}`,
        label: formatSessionDividerLabel(message.createdAt),
      });
    }

    items.push({
      kind: 'message',
      key: `message-${message.id}`,
      message,
      index,
    });
  });

  return items;
});

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Find in-reply-to message in the messages prop
  const replyMessage = props.messages?.find(
    message => message.id === inReplyToMessageId
  );

  return replyMessage ? useCamelCase(replyMessage) : null;
};
</script>

<template>
  <ul class="px-4" style="background-color: var(--wlab-bg-primary)">
    <slot name="beforeAll" />
    <template v-for="item in renderItems" :key="item.key">
      <li v-if="item.kind === 'divider'" class="wlab-convo-divider">
        <div class="wlab-convo-divider__line" />
        <div class="wlab-convo-divider__pill">
          <p class="wlab-convo-divider__text">{{ item.label }}</p>
        </div>
        <div class="wlab-convo-divider__line" />
      </li>
      <template v-else>
        <slot
          v-if="firstUnreadId && item.message.id === firstUnreadId"
          name="unreadBadge"
        />
        <Message
          v-bind="item.message"
          :is-email-inbox="isAnEmailChannel"
          :in-reply-to="getInReplyToMessage(item.message)"
          :group-with-next="shouldGroupWithNext(item.index, allMessages)"
          :group-with-prev="shouldGroupWithPrev(item.index, allMessages)"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :current-user-id="currentUserId"
          data-clarity-mask="True"
          @retry="emit('retry', item.message)"
        />
      </template>
    </template>
    <slot name="after" />
  </ul>
</template>
